import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons';

import './nimbusSelect.scss';
import { renderableType } from '../proptypes/prop-types';
import { buildClassNameModifiers, buildClassNames, clampAndWrap, px } from '../util/utils';
import { CircularProgress } from '../animations/circularProgress';
import SelectItemList from './selectItemList';
import SelectItem from './selectItem';
import { ValueTextRenderer } from './valueTextRenderer';
import { ValueTokenRenderer } from './valueTokenRenderer';
// Block class
const blockBaseClassName = 'select';

// Element classes
const containerClassName = 'select__container';
const iconClassName = 'select__icon';
const inputClassName = 'select__input';
const inputWrapperClassName = 'select__inputWrapper';
const labelClassName = 'select__label';
const listClassName = 'select__list';
const messageClassName = 'select__message';
const removeAllIconClassName = 'select__removeAllIcon';
const selectItemClassName = 'select__selectItem';
const valueClassName = 'select__value';

// Modifiers
const errorModifier = 'error';
const focusedModifier = 'focused';
const hasValueModifier = 'hasValue';
const multiModifier = 'multi';
const noLabelModifier = 'noLabel';
const noOptionsModifier = 'noOptions';
const openModifier = 'open';

const loadingOptions = (
  <div className={buildClassNameModifiers(selectItemClassName, noOptionsModifier)}>
    Loading...
  </div>);
const noOptionsAvailable = (
  <div className={buildClassNameModifiers(selectItemClassName, noOptionsModifier)}>
    No options available
  </div>);
const searchForOptions = (
  <div className={buildClassNameModifiers(selectItemClassName, noOptionsModifier)}>
    Enter search for options...
  </div>);

/**
 * A component that supports single select or multi-select with tokens
 */
export  class NimbusSelect extends Component {
  static propTypes = {
    /** @ignore */
    className: PropTypes.string,
    /** Determines whether the item list uses autocomplete */
    autocomplete: PropTypes.bool,
    /** Determines whether the component will be focused by default once the page loads */
    autoFocus: PropTypes.bool,
    /** Determines whether the component will be rendered in a compact form */
    compact: PropTypes.bool,
    /** Indicates whether the component is disabled */
    disabled: PropTypes.bool,
    /** Indicates whether the component is in an error state */
    error: PropTypes.bool,
    /** Text to be rendered when the component is in an error state */
    errorMessage: PropTypes.string,
    /** Indicates that options will be hidden until the user performs a text search */
    hideOptionsUntilSearch: PropTypes.bool,
    /** Determines whether selected items are hidden from the item list */
    hideSelected: PropTypes.bool,
    /** The id assigned to the input */
    id: PropTypes.string,
    /** Indicates that the select component is in a loading state */
    isLoading: PropTypes.bool,
    /** Determines whether the item list will remain open once a selection is made */
    keepOpenOnSelect: PropTypes.bool,
    /** The label associated with the select input */
    label: PropTypes.string,
    /** Text to be rendered when the component is not in an error state */
    message: PropTypes.string,
    /** Determines whether multiple selections are allowed */
    multi: PropTypes.bool,
    /** Function executed when the search input changes */
    onInput: PropTypes.func,
    /** Function executed when a selection is removed */
    onRemove: PropTypes.func,
    /** Function executed when all selections are removed */
    onRemoveAll: PropTypes.func,
    /** Function executed when user inputs search terms */
    onSearch: PropTypes.func,
    /** Function executed when a selection is made */
    onSelect: PropTypes.func.isRequired,
    /** Items available for selection */
    options: PropTypes.arrayOf(PropTypes.shape({
      /** The rendered representation of the option */
      label: PropTypes.string.isRequired,
      /** The value of the option */
      value: PropTypes.any.isRequired,
    })),
    /** Placeholder text for the search field */
    placeholder: PropTypes.string,
    /** Determines whether selected options can be removed */
    removable: PropTypes.bool,
    /** Determines whether the item list is searchable */
    searchable: PropTypes.bool,
    /** Component that will be used to render the select items in the item list */
    selectItemComponent: PropTypes.func,
    /** Items that have been selected */
    value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
    /** Renderer used for the selected values.
     *
     * Single select components default to a text-based renderer.
     * Multi select components default to a token-based renderer.
     */
    valueRenderer: PropTypes.oneOfType([PropTypes.oneOf(['text', 'token']), renderableType]),
  };

  static defaultProps = {
    autoFocus: false,
    compact: false,
    disabled: false,
    hideOptionsUntilSearch: false,
    hideSelected: false,
    isLoading: false,
    keepOpenOnSelect: false,
    multi: false,
    options: [],
    placeholder: 'Select...',
    removable: false,
    searchable: false,
    selectItemComponent: SelectItem,
    value: undefined,
  };

  constructor(props) {
    super(props);
    this.state = {
      focusedOption: undefined,
      inputFocused: false,
      isOpen: false,
      loading: false,
      searchText: '',
      willSearch: false,
    };
  }

  componentDidMount() {
    if (this.props.autoFocus) {
      this.focusOnInput();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.isOpen !== this.state.isOpen) {
      this.toggleClickOutsideEvent(true);
    }
    if (this.list && this.focused && this.state.isOpen) {
      this.scrollToElement();
    }
  }

  componentWillUnmount() {
    this.toggleClickOutsideEvent(false);
  }

  setInputRef = (ip) => {
    this.inputNode = ip;
  }

  setContainerRef = (cont) => {
    this.containerNode = cont;
  }

  toggleClickOutsideEvent(enabled) {
    if (enabled) {
      document.addEventListener('click', this.handleClickOutside, true);
    } else {
      document.removeEventListener('click', this.handleClickOutside, true);
    }
  }

  handleClickOutside = (event) => {
    if (this.state.isOpen) {
      if (this.containerNode && !this.containerNode.contains(event.target)) {
        this.hide();
      }
    }
  }

  handleSelect = option => (event) => {
    const nextOption = this.focusOnNextOption(option);
    if (nextOption) {
      event.preventDefault();
      event.stopPropagation();
      this.setState(
        (prevState, props) => (
          {
            focusedOption: props.keepOpenOnSelect ? nextOption : undefined,
            inputFocused: props.keepOpenOnSelect,
            isOpen: props.keepOpenOnSelect,
          }),
        () => this.props.onSelect(event, option.option),
      );
    }
  };

  handleOpen = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({
      inputFocused: true,
      isOpen: true,
    });
  };

  handleClose = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({
      inputFocused: false,
      isOpen: false,
    });
  };


  hide = () => {
    this.setState({
      inputFocused: false,
      isOpen: false,
    });
  }

  handleRemove = option => (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState(
      {
        inputFocused: true,
        isOpen: true,
      },
      () => this.props.onRemove(option.option || option),
    );
  };

  handleRemoveAll = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState(
      {
        inputFocused: true,
        isOpen: true,
      },
      () => this.props.onRemoveAll(),
    );
  };

  debouncedOnSearch = (function() {
    let timeout;
    return function(newSearchText) {
      clearTimeout(timeout);
      timeout = setTimeout(async () => {
        await this.setState({ focusedOption: undefined, loading: true });
        if (this.props.onSearch) await this.props.onSearch(newSearchText);
        await this.setState({
          isOpen: true,
          loading: false,
          willSearch: false,
        });
      }, 250);
    };
  })();

  handleSearchInputChange = (event) => {
    const newSearchText = event.target.value;
    this.setState(
      {
        searchText: newSearchText,
        willSearch: newSearchText && newSearchText.length > 0,
      },
      () => this.debouncedOnSearch(newSearchText),
    );
  };

  handleSearchInputClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  scrollToElement = () => {
    if (this.list && this.focused) {
      const listTop = this.list.scrollTop;
      const listBottom = listTop + this.list.offsetHeight;
      const elementTop = this.focused.offsetTop;
      const elementBottom = elementTop + this.focused.offsetHeight;
      if (elementBottom > listBottom) {
        this.list.scrollTop = ((elementTop + this.focused.clientHeight) - this.list.offsetHeight);
      } else if (elementTop < listTop) {
        this.list.scrollTop = elementTop;
      }
    }
  }

  handleKeyDown = (event) => {
    const {
      disabled,
      value,
    } = this.props;
    if (!disabled && this.state.isOpen) {
      switch (event.key) {
        case 'ArrowDown':
          event.stopPropagation();
          event.preventDefault();
          this.setState({ focusedOption: this.focusOnNextOption(this.state.focusedOption) });
          break;
        case 'ArrowUp':
          event.stopPropagation();
          event.preventDefault();
          this.setState({ focusedOption: this.focusOnPrevOption(this.state.focusedOption) });
          break;
        case 'Enter':
          event.stopPropagation();
          event.preventDefault();
          this.handleSelect(this.state.focusedOption
            || this.focusOnNextOption(this.state.focusedOption))(event);
          break;
        case 'Backspace':
          if (this.state.searchText === '') {
            if (Array.isArray(value)) {
              if (value && value.length > 0) {
                this.handleRemove(value[value.length - 1])(event);
              }
            } else if (value) {
              this.handleRemove(value)(event);
            }
          }
          break;
        case 'Tab':
          this.setState({
            focusedOption: undefined,
            inputFocused: false,
            isOpen: false,
          });
          break;
        default:
          break;
      }
    }
  }

  handleValueKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      if (this.props.searchable) {
        this.focusOnInput(e);
      } else {
        this.handleOpen(e);
      }
    }
  }

  handleRemoveAllKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      if (this.props.removable) {
        this.handleRemoveAll(e);
      }
    }
  }

  handleSelectIconKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      if (this.props.searchable) {
        this.focusOnInput(e);
      } else {
        this.handleOpen(e);
      }
    }
  }

  focusOnNextOption = (focusedOption) => {
    const {
      options,
      value,
    } = this.props;

    if (options.filter(option => !this.isSelected(option)(value)).length === 0) {
      return undefined;
    }
    if (typeof focusedOption === 'undefined') {
      const ind = options.findIndex(option => !this.isSelected(option)(value));
      return {
        index: ind,
        option: options[ind],
      };
    }

    const tempIndex = clampAndWrap(0)(options.length - 1)(focusedOption.index + 1);
    const newIndex = this.findIndexFrom(options, tempIndex, option =>
        !this.isSelected(option)(value))
      || this.findLastIndexFrom(options, tempIndex, option => !this.isSelected(option)(value));
    return {
      index: newIndex,
      option: options[newIndex],
    };
  }

  focusOnPrevOption = (focusedOption) => {
    const {
      options,
      value,
    } = this.props;

    if (options.filter(option => !this.isSelected(option)(value)).length === 0) {
      return undefined;
    }

    if (typeof focusedOption === 'undefined') {
      const ind = this.findLastIndex(options, option => !this.isSelected(option)(value));
      return {
        index: ind,
        option: options[ind],
      };
    }

    const tempIndex = clampAndWrap(0)(options.length - 1)(focusedOption.index - 1);
    const newIndex = this.findLastIndexFrom(options, tempIndex, option =>
        !this.isSelected(option)(value))
      || this.findIndexFrom(options, tempIndex, option => !this.isSelected(option)(value));
    return {
      index: newIndex,
      option: options[newIndex],
    };
  }

  findIndexFrom = (array, index, predicate) => {
    for (let i = index; i < array.length; i++) {
      if (predicate(array[i])) {
        return i;
      }
    }
    return -1;
  }

  findLastIndex = (array, predicate) => {
    for (let i = array.length - 1; i >= 0; i--) {
      if (predicate(array[i])) {
        return i;
      }
    }
    return -1;
  }

  findLastIndexFrom = (array, index, predicate) => {
    for (let i = index; i >= 0; i--) {
      if (predicate(array[i])) {
        return i;
      }
    }
    return -1;
  }

  focusOnInput = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.props.disabled) return;
    this.setState(
      {
        inputFocused: true,
        isOpen: true,
      },
      () => this.inputNode.focus(),
    );
  }

  handleFocus = (event) => {
    this.focusOnInput(event);
  }

  handleItemRef = (ref, focused) => {
    if (focused && ref) {
      this.focused = ref;
    }
  }

  handleListRef = (ref) => {
    if (ref) {
      this.list = ref;
    }
  }

  isSelected = option => (selected) => {
    if (Array.isArray(selected)) {
      return selected.map(selection => selection.label).includes(option.label);
    }

    return option.label === (selected ? selected.label : '');
  }

  isAwaitingSearch = () => {
    const { hideOptionsUntilSearch } = this.props;
    const {
      searchText,
      willSearch,
    } = this.state;
    return hideOptionsUntilSearch && (!searchText || willSearch);
  }

  renderValue = () => {
    const {
      disabled,
      multi,
      onRemove,
      removable,
      searchable,
      value,
      valueRenderer: ValueRenderer,
    } = this.props;
    const {
      inputFocused,
      isOpen,
    } = this.state;
    if (ValueRenderer && ValueRenderer !== 'text' && ValueRenderer !== 'token') {
      return (<ValueRenderer {...this.props} inputFocused={inputFocused} isOpen={isOpen} />);
    }
    if ((multi && !ValueRenderer) || ValueRenderer === 'token') {
      return (
        <ValueTokenRenderer
          value={value}
          disabled={disabled}
          valueClassName={valueClassName}
          removable={removable && onRemove && !disabled}
          onRemove={this.handleRemove}
        />
      );
    }
    return (
      <ValueTextRenderer
        value={value}
        valueClassName={buildClassNames(
          buildClassNameModifiers(
            valueClassName,
            { disabled },
            { searchable },
            (isOpen || inputFocused) && focusedModifier,
          ),
          valueClassName,
        )}
      />
    );
  }

  renderInput = () => {
    const {
      autocomplete,
      disabled,
      id,
      label,
      onInput,
      placeholder,
      value,
    } = this.props;
    const {
      inputFocused,
      isOpen,
      searchText,
    } = this.state;
    const hasValue = Array.isArray(value) ? value && value.length > 0 : value;
    return (<input
      id={id}
      tabIndex='0'
      style={{ width: searchText ? px(5 * searchText.length) : '5px' }}
      disabled={disabled}
      autoComplete={autocomplete && autocomplete.toString()}
      ref={this.setInputRef}
      placeholder={!hasValue ? placeholder : ''}
      className={buildClassNames(
        buildClassNameModifiers(
          inputClassName,
          { disabled },
          (isOpen || inputFocused) && focusedModifier,
          !label && noLabelModifier,
          hasValue && hasValueModifier,
        ),
        inputClassName,
      )}
      onInput={onInput}
      onFocus={this.handleFocus}
      onChange={!disabled ? this.handleSearchInputChange : undefined}
      onKeyDown={this.handleKeyDown}
      value={searchText}
    />);
  }

  renderRemoveAllIcon = (removeHandler) => {
    const {
      disabled,
      value,
    } = this.props;
    let toRender = value;
    if (Array.isArray(value)) {
      toRender = toRender && value.length > 0;
    }
    const removeAllIconClassNames = buildClassNames(
      buildClassNameModifiers(removeAllIconClassName, { disabled }),
      removeAllIconClassName,
    );
    return toRender && (
      <span
        className={removeAllIconClassNames}
        onClick={!disabled ? removeHandler : undefined}
        onKeyDown={!disabled ? this.handleRemoveAllKeyDown : undefined}
      >
          <FontAwesomeIcon icon={faTimesCircle} />
        </span>);
  }

  renderSelectIcon = (clickHandler) => {
    const { disabled, isLoading } = this.props;
    const {
      isOpen,
      loading,
    } = this.state;
    const iconClassNames = buildClassNames(
      buildClassNameModifiers(iconClassName, { disabled }, isOpen && openModifier),
      iconClassName,
    );
    return (
      <CircularProgress size={15} show={loading || isLoading} style={{ display: 'flex' }}>
          <span
            className={iconClassNames}
            onClick={!disabled ? clickHandler : undefined}
            onKeyDown={!disabled ? this.handleSelectIconKeyDown : undefined}
          >
            <FontAwesomeIcon icon={faCaretDown} />
          </span>
      </CircularProgress>
    );
  }

  renderSelectItems = () => {
    const {
      hideSelected,
      isLoading,
      options,
      removable,
      selectItemComponent,
      value,
    } = this.props;
    const {
      focusedOption,
      loading,
    } = this.state;

    if (loading || isLoading) return loadingOptions;
    if (this.isAwaitingSearch()) return searchForOptions;

    const visibleOptions = (this.isAwaitingSearch() ? [] : options)
      .filter(option => (hideSelected ? !this.isSelected(option)(value) : option));

    if (!visibleOptions || visibleOptions.length === 0) {
      return noOptionsAvailable;
    }

    const SelectItemComp = selectItemComponent;
    const SelectItemCompProps = selectItemComponent.props;
    return visibleOptions.map((option, index) => {
      const selected = this.isSelected(option)(value);
      return (
        <SelectItemComp
          index={index}
          itemRef={this.handleItemRef}
          role='option'
          aria-label={option.label}
          selected={selected}
          focused={focusedOption && (focusedOption.option === option)}
          className={selectItemClassName}
          disabled={!hideSelected && selected}
          key={`option-${option && option.label ? option.label.replace(/\s+/g, '') : ''}`}
          option={option}
          onSelect={this.handleSelect({ index, option })}
          onRemove={removable ? this.handleRemove(option) : undefined}
          {...SelectItemCompProps}
        />
      );
    });
  }

  render() {
    const {
      className,
      autocomplete,
      autoFocus,
      compact,
      disabled,
      error,
      errorMessage,
      hideOptionsUntilSearch,
      hideSelected,
      id,
      isLoading,
      keepOpenOnSelect,
      label,
      message,
      multi,
      onRemove,
      onRemoveAll,
      onSearch,
      onSelect,
      options,
      placeholder,
      removable,
      searchable,
      selectItemComponent,
      value,
      valueRenderer,
      ...other
    } = this.props;
    const {
      inputFocused,
      isOpen,
    } = this.state;
    const hasValue = Array.isArray(value) ? value && value.length > 0 : value;
    const blockClassNames = buildClassNames(
      className,
      buildClassNameModifiers(
        blockBaseClassName,
        error && errorModifier,
        { disabled },
        { compact },
      ),
      blockBaseClassName,
    );
    const labelClassNames = buildClassNames(
      buildClassNameModifiers(
        labelClassName,
        { disabled },
        { compact },
        error && errorModifier,
        isOpen && openModifier,
        (isOpen || inputFocused) && focusedModifier,
        hasValue && hasValueModifier,
      ),
      labelClassName,
    );
    const containerClassNames = buildClassNames(
      buildClassNameModifiers(
        containerClassName,
        { disabled },
        { compact },
        error && errorModifier,
        isOpen && openModifier,
        (isOpen || inputFocused) && focusedModifier,
      ),
      containerClassName,
    );
    const listClassNames = buildClassNames(
      buildClassNameModifiers(listClassName, isOpen && openModifier),
      listClassName,
    );
    const inputWrapperClassNames = buildClassNames(
      buildClassNameModifiers(
        inputWrapperClassName,
        { compact },
        multi && multiModifier,
      ),
      inputWrapperClassName,
    );
    const messageClassNames = buildClassNames(
      buildClassNameModifiers(
        messageClassName,
        { disabled },
        { compact },
        error && errorModifier,
      ),
      messageClassName,
    );
    /* eslint-disable no-nested-ternary */
    const clickHandler = disabled
      ? undefined
      : (searchable ? this.focusOnInput : this.handleOpen);
    /* eslint-enable no-nested-ternary */
    const removeHandler = (disabled || !removable) ? undefined : this.handleRemoveAll;

    return (
      <div
        role='listbox'
        aria-haspopup='listbox'
        tabIndex={0}
        className={blockClassNames}
        onFocus={!searchable ? clickHandler : undefined}
        onClick={clickHandler}
        onKeyDown={!searchable ? this.handleKeyDown : undefined}
        ref={this.setContainerRef}
        {...other}
      >
        {!(compact && (hasValue || inputFocused)) &&
          label && (
            <label className={labelClassNames} htmlFor={id} title={label}>{label}</label>
          )}
        <div className={containerClassNames}>
          <div className={inputWrapperClassNames}>
            {this.renderValue()}
            {searchable && this.renderInput()}
          </div>
          {(!disabled && removable) && this.renderRemoveAllIcon(removeHandler)}
          {this.renderSelectIcon(isOpen ? this.handleClose : clickHandler)}
        </div>
        {!disabled &&
          <SelectItemList className={listClassNames} listRef={this.handleListRef}>
            {this.renderSelectItems()}
          </SelectItemList>
        }
        {(!compact || (error && errorMessage) || message) &&
          <div className={messageClassNames}>
            {error ? errorMessage : message}
          </div>
        }
      </div>
    );
  }
}
